// Tax Center → Documents: every stored document for the year, filterable,
// with view (inline PDF — the draft preview), download, and "issue
// correction" for issued documents.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingState from '../common/LoadingState';
import { listDocuments, correctDocument, documentUrl, errorText } from '../../services/taxApi';
import { when } from './format';

const TYPES = [
  'giving_statement', 'donor_acknowledgment', 'quid_pro_quo', 'w2g_recipient', 'revenue_summary_pdf', 'revenue_summary_csv',
  'revenue_ledger_csv', 'gaming_summary_pdf', 'gaming_summary_csv', 'w2g_filer_packet', 'w2g_summary_csv', 'subscription_invoice',
];

export default function TaxDocuments({ year, canIssue, onChanged }) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({ scope: '', type: '', recipient: '', drafts: false });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      setData(await listDocuments(year, { ...filters, page }));
    } catch (err) {
      setError(errorText(err, t('tax.docs.loadError', 'Could not load documents.')));
    }
  }, [year, filters, page, t]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => { setPage(1); setFilters((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })); };

  const correct = async (d) => {
    if (!window.confirm(t('tax.docs.confirmCorrect', 'Issue a corrected version of “{{title}}” from the current data? The original stays on record.', { title: d.title }))) return;
    try {
      await correctDocument(d.id);
      setMsg(t('tax.docs.correctQueued', 'Correction queued. It appears here as a new version when ready.'));
      onChanged && onChanged();
    } catch (err) {
      setMsg(errorText(err, t('tax.docs.correctError', 'Could not queue the correction.')));
    }
  };

  const typeLabel = (k) => t(`tax.docType.${k}`, k.replace(/_/g, ' '));
  const statusLabel = { draft: t('tax.mode.draft', 'Draft'), final: t('tax.mode.final', 'Final'), corrected: t('tax.status.corrected', 'Corrected') };

  return (
    <section className="surface" aria-labelledby="tx-docs-title">
      <h4 id="tx-docs-title">{t('tax.docs.title', 'Documents for {{year}}', { year })}</h4>
      <div className="tx-filters">
        <label className="fo-field">{t('tax.docs.audience', 'For')}
          <select value={filters.scope} onChange={set('scope')}>
            <option value="">{t('tax.docs.everyone', 'Everyone')}</option>
            <option value="admin">{t('tax.docs.organization', 'Your organization')}</option>
            <option value="recipients">{t('tax.docs.recipients', 'Donors & winners')}</option>
          </select>
        </label>
        <label className="fo-field">{t('tax.docs.type', 'Type')}
          <select value={filters.type} onChange={set('type')}>
            <option value="">{t('tax.docs.allTypes', 'All types')}</option>
            {TYPES.map((k) => <option key={k} value={k}>{typeLabel(k)}</option>)}
          </select>
        </label>
        <label className="fo-field">{t('tax.docs.search', 'Recipient email')}
          <input type="search" value={filters.recipient} onChange={set('recipient')} />
        </label>
        <label className="fo-check"><input type="checkbox" checked={filters.drafts} onChange={set('drafts')} /> {t('tax.docs.showDrafts', 'Show drafts')}</label>
      </div>
      {msg && <div className="note info" role="status">{msg}</div>}
      {error && <div className="note warn">{error}</div>}
      {!data && !error && <LoadingState />}
      {data && (
        data.documents.length === 0
          ? <p className="surface-muted perf-small">{t('tax.docs.empty', 'No documents yet. Generate a draft from the Overview tab.')}</p>
          : (
            <div className="perf-table-wrap">
              <table className="perf-table tx-table">
                <thead>
                  <tr>
                    <th>{t('tax.docs.document', 'Document')}</th>
                    <th>{t('tax.docs.recipient', 'Recipient')}</th>
                    <th>{t('tax.docs.status', 'Status')}</th>
                    <th>{t('tax.docs.generated', 'Generated')}</th>
                    <th><span className="sr-only">{t('tax.docs.actions', 'Actions')}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {data.documents.map((d) => (
                    <tr key={d.id}>
                      <td>{d.title}{d.containsTin ? ` · ${t('tax.docs.hasTin', 'contains TINs')}` : ''}</td>
                      <td>{d.recipientEmail || t('tax.docs.organization', 'Your organization')}</td>
                      <td><span className={`tx-status ${d.status}`}>{statusLabel[d.status] || d.status}{d.version > 1 ? ` v${d.version}` : ''}</span></td>
                      <td>{when(d.generatedAt)}{d.deliveredAt ? ` · ${t('tax.docs.sent', 'sent')}` : ''}</td>
                      <td>
                        <div className="lm-actions">
                          {/\.pdf$/i.test(d.fileName) && <a href={documentUrl(d.id, true)} target="_blank" rel="noopener noreferrer">{t('tax.docs.view', 'View')}</a>}
                          <a href={documentUrl(d.id)}>{t('tax.docs.download', 'Download')}</a>
                          {canIssue && d.status !== 'draft' && <button type="button" className="lm-btn secondary" onClick={() => correct(d)}>{t('tax.docs.correct', 'Correct')}</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}
      {data && (page > 1 || data.hasMore) && (
        <div className="tx-actions" style={{ marginTop: 8 }}>
          <button type="button" className="lm-btn secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t('common.previous', 'Previous')}</button>
          <button type="button" className="lm-btn secondary" disabled={!data.hasMore} onClick={() => setPage((p) => p + 1)}>{t('common.next', 'Next')}</button>
        </div>
      )}
    </section>
  );
}
